import { Controller } from '@hotwired/stimulus';
import { cancel } from 'helpers/dom-helper';
import useResetForm from 'stimulus-hooks/use-reset-form';

const CURRENT_OPTION_RADIO_VALUE = '1';
const FOLLOWING_OPTION_RADIO_VALUE = '2';

export default class extends Controller<HTMLElement> {
  static targets = ['form', 'allOption', 'currentOption', 'radio'];

  static values = {
    sameDay: { type: Boolean, default: true },
    sameRrule: { type: Boolean, default: true }
  };

  connect(): void {
    useResetForm(this, { element: this.formTarget });
  }

  // We want to force check the radio button, else it will be stateless.
  modalShown() {
    this.sameRruleValueChanged(this.sameRruleValue);
  }

  sameDayValueChanged(value: boolean) {
    if (this.hasAllOptionTarget) {
      this.allOptionTarget.hidden = !value;
    }
  }

  sameRruleValueChanged(value: boolean) {
    if (!this.currentOptionRadio || !this.followingOptionRadio) return;

    if (value) {
      this.currentOptionTarget.hidden = false;
      this.currentOptionRadio.checked = true;
    } else {
      this.currentOptionTarget.hidden = true;
      this.followingOptionRadio.checked = true;
    }
  }

  submit(event: Event) {
    cancel(event);

    const { target } = event;
    if (!(target instanceof HTMLFormElement)) return;

    const formData = new FormData(target);
    const updateType = formData.get('event[update_type]');

    // Show saving state before dispatching the event
    this.showSavingState();

    this.dispatch('submit', {
      detail: { updateType, targetId: this.targetFormId },
      bubbles: true
    });

    $(this.element).modal('hide');
  }

  // New method to show saving state
  showSavingState() {
    // Get the target form ID
    const formId = this.targetFormId;
    if (!formId) return;

    // Find the target form
    const formElement = document.getElementById(formId);
    if (!formElement) return;

    // Find the modal content that contains the form
    const modalContent = formElement.closest('.modal-content');
    if (!modalContent) return;

    // Look for existing saving state element
    let savingState = modalContent.querySelector('#event-saving-state');
    let formContainer = modalContent.querySelector('#event-form-container');

    // If saving state doesn't exist, create it
    if (!savingState) {
      // Create saving state element
      savingState = document.createElement('div');
      savingState.id = 'event-saving-state';
      savingState.className = 'modal-body p-5 text-center';
      savingState.innerHTML = `
        <div class="d-flex flex-column align-items-center justify-content-center py-4">
          <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status">
            <span class="sr-only">Saving...</span>
          </div>
          <h4 class="mt-3 mb-2">Saving your changes</h4>
          <p class="text-muted">Please wait while we update your event...</p>
        </div>
      `;

      // Create form container if it doesn't exist
      if (!formContainer) {
        formContainer = document.createElement('div');
        formContainer.id = 'event-form-container';

        // Find the form
        const form = modalContent.querySelector('form');
        if (form) {
          // Insert container before form
          form.parentNode.insertBefore(formContainer, form);
          // Move form into container
          formContainer.appendChild(form);
        }
      }

      // Insert saving state before form container
      if (formContainer.parentNode) {
        formContainer.parentNode.insertBefore(savingState, formContainer);
      }
    }

    // Show saving state and hide form
    if (savingState && formContainer) {
      formContainer.style.display = 'none';
      savingState.style.display = 'block';
    }
  }

  private resetForm(): void {
    this.element.removeAttribute('data-form-id');
  }

  private get targetFormId() {
    return this.element.dataset.formId;
  }

  private get currentOptionRadio() {
    return this.radioTargets.find(({ value }) => value === CURRENT_OPTION_RADIO_VALUE);
  }

  private get followingOptionRadio() {
    return this.radioTargets.find(({ value }) => value === FOLLOWING_OPTION_RADIO_VALUE);
  }

  declare readonly sameRruleValue: boolean;
  declare readonly formTarget: HTMLFormElement;
  declare readonly allOptionTarget: HTMLElement;
  declare readonly hasAllOptionTarget: boolean;
  declare readonly currentOptionTarget: HTMLElement;
  declare readonly radioTargets: HTMLInputElement[];
}